import {
  ApiAttractionAvailabilityInfo,
  ApiFilter,
  ApiFilterCategory,
  ApiFilterType,
  ApiGroupedMultipleSelectionFilter,
  ApiMultipleSelectionFilter,
  ApiNumberRangeFilter,
  ApiRoomContainer,
  ApiSingleSelectionFilter,
  ApiSorting,
  ApiSortOption
} from '@ibe/api';
import { faUtensils, faBed, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import styled from '@emotion/styled';
import { ReactElement, ReactNode, RefObject, JSX } from 'react';
import { PaginationProps } from '../../Components/CustomPagination/CustomPagination';
import { MapConfig, Marker, MapRefType } from '../../Components/UI/Maps/MapsInterfaces';
import { FilterOverrides } from '../../Components/DynamicList/FilterOptions/FilterOptions';
import CustomFilter from '../../../Models/CustomFilter';
import HotelStore from '../../Modules/Hotel/HotelStore';

export interface ServiceListProps<I extends { id: string }> {
  store?: HotelStore;
  items: I[];
  selectedItemId?: string;
  paginationProps?: PaginationProps;
  sortProps?: SortingProperties;
  renderItem: (item: I, idx: number, occupancy?: ApiRoomContainer[]) => ReactElement;
  fullItemCount?: number;
  filterProps?: FilterProperties;
  filterOverrides?: FilterOverrides;
  filteredItemCount?: number;
  mapConfig?: MapConfig;
  additionalContent?: Array<{ id: number; component: JSX.Element }>;
  additionalContentHeader?: string;
  occupancy?: ApiRoomContainer[] | undefined;
  useIcons?: boolean;
  useLazyLoading?: boolean;
  externalTopContent?: ReactNode;
  closureDates?: ApiAttractionAvailabilityInfo[];
  customListOptions?: CustomListOptions;
  ExtendedMapMarkerPopup?: (maker: Marker) => JSX.Element;
  mapRef?: RefObject<MapRefType>;
  filterLabelTranslationMappings?: Record<string, (label: string) => string>;
  customFilter?: CustomFilter;
}

export interface FilterState {
  isOpen: boolean;
  onChange: (value: boolean) => void;
}

export interface FilterProperties {
  filter: ApiFilter[];
  onFilterChange: (
    filter:
      | ApiMultipleSelectionFilter
      | ApiNumberRangeFilter
      | ApiGroupedMultipleSelectionFilter
      | ApiSingleSelectionFilter
  ) => void;
  state: FilterState;
  onFiltersDiscard: () => void;
  singleSelectCategories?: boolean;
}

export interface SortingProperties {
  onChange: (sorting: ApiSorting | undefined) => void;
  sorting: ApiSorting | undefined;
  sortingOptions: ApiSortOption[];
  defaultSorting?: ApiSorting;
  sortingType?: {
    GROUP: ApiSortOption[];
    SINGLE: ApiSortOption[];
  };
  customListOptions?: CustomListOptions;
  hidden?: boolean;
}

export type CustomListOptions = {
  showFilterInModal?: boolean;
  showResultsCountInParenthesis?: boolean;
  showResultsCount?: Partial<Record<ApiFilterCategory, boolean>>;
  showEmptyOptionsWhenFiltering?: boolean;
  showApplyButtonInModal?: boolean;
  sortingSimpleLabel?: 'ALL_BREAKPOINTS' | 'MOBILE';
  closeSorterOnClick?: boolean;
  disabledSortOptions?: ApiSortOption[];
  customIcons?: {
    sort?: IconDefinition;
    map?: IconDefinition;
    filter?: IconDefinition;
  };
  showResetButtonForFilter?: boolean;
};

export const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const FilterContainer = styled.div`
  flex-shrink: 0;
  height: 100%;
  min-width: 16rem;
`;

const calculateAppliedFiltersCount = (filterProps?: FilterProperties): number => {
  let appliedFiltersCount = 0;
  filterProps?.filter.forEach(filter => {
    let castedFilter:
      | ApiMultipleSelectionFilter
      | ApiGroupedMultipleSelectionFilter
      | ApiSingleSelectionFilter
      | ApiNumberRangeFilter;
    let hasMinMaxSelected: boolean;
    switch (filter.type) {
      case ApiFilterType.MultipleSelectionFilter:
        castedFilter = filter as ApiMultipleSelectionFilter;
        if (castedFilter.selectedOptions) {
          appliedFiltersCount += castedFilter.selectedOptions.length;
        }
        break;
      case ApiFilterType.GroupedMultipleSelectionFilter:
        castedFilter = filter as ApiGroupedMultipleSelectionFilter;
        if (castedFilter.selectedOptions) {
          appliedFiltersCount += castedFilter.selectedOptions.length;
        }
        break;
      case ApiFilterType.SingleSelectionFilter:
        castedFilter = filter as ApiSingleSelectionFilter;
        if (castedFilter.selectedOption) {
          appliedFiltersCount += 1;
        }
        break;
      case ApiFilterType.NumberRangeFilter:
        castedFilter = filter as ApiNumberRangeFilter;
        hasMinMaxSelected =
          castedFilter.min === castedFilter.minSelected &&
          castedFilter.max === castedFilter.maxSelected;
        if (
          (castedFilter.minSelected !== undefined || castedFilter.maxSelected !== undefined) &&
          !hasMinMaxSelected
        ) {
          appliedFiltersCount += 1;
        } else if (appliedFiltersCount > 0 && hasMinMaxSelected) {
          appliedFiltersCount -= 1;
        }
        break;
      default:
        break;
    }
  });
  return appliedFiltersCount;
};

export const getIcon = (filter: ApiFilter, useIcons?: boolean): IconDefinition | undefined => {
  if (!useIcons) return undefined;

  switch (filter.category) {
    case ApiFilterCategory.SERVICEOPTIONS:
      return faUtensils;
    case ApiFilterCategory.UNITTYPE:
      return faBed;
    default:
      return undefined;
  }
};

export default calculateAppliedFiltersCount;
